import React from "react";
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Grid,
  Paper,
  Icon,
  useTheme,
  alpha,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";

export const Security: React.FC = () => {
  const theme = useTheme();

  const securityFeatures = [
    {
      title: "Enterprise-Grade Encryption",
      icon: "security",
      description:
        "Bank-level encryption for all data in transit and at rest. We use AES-256 encryption and SSL/TLS protocols to ensure maximum security.",
      highlights: [
        "AES-256 Encryption",
        "SSL/TLS Protocols",
        "Secure Key Management",
      ],
    },
    {
      title: "Advanced Access Control",
      icon: "admin_panel_settings",
      description:
        "Granular role-based access control with multi-factor authentication and detailed audit logs of all system activities.",
      highlights: ["Role-Based Access", "2FA/MFA Support", "Activity Logging"],
    },
    {
      title: "Compliance & Certifications",
      icon: "verified_user",
      description:
        "Fully compliant with international security standards and regularly audited to maintain the highest level of security.",
      highlights: ["PCI DSS", "GDPR", "ISO 27001"],
    },
    {
      title: "24/7 Monitoring",
      icon: "monitoring",
      description:
        "Continuous system monitoring with automated threat detection and instant alerts for any suspicious activities.",
      highlights: [
        "Real-time Monitoring",
        "Threat Detection",
        "Instant Alerts",
      ],
    },
  ];

  return (
    <Box sx={{ pb: 8 }}>
      <Box
        sx={{
          background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "white",
          py: 6,
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Breadcrumbs
            sx={{
              mb: 4,
              "& .MuiBreadcrumbs-separator": { color: "white" },
              "& a": { color: "white", opacity: 0.8 },
            }}
          >
            <Link component={RouterLink} to="/">
              Home
            </Link>
            <Typography color="white">Security</Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 2,
            }}
          >
            Enterprise-Grade Security
          </Typography>

          <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 600 }}>
            Your security is our top priority. We implement multiple layers of
            protection to keep your data safe and your business secure.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {securityFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={feature.title}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Paper
                  sx={{
                    p: 4,
                    height: "100%",
                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    >
                      <Icon
                        className="material-icons-outlined"
                        sx={{
                          color: "primary.main",
                          fontSize: 28,
                        }}
                      >
                        {feature.icon}
                      </Icon>
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                  </Box>

                  <Typography
                    color="text.secondary"
                    sx={{
                      lineHeight: 1.7,
                      mb: 3,
                    }}
                  >
                    {feature.description}
                  </Typography>

                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {feature.highlights.map((highlight) => (
                      <Box
                        key={highlight}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1,
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          color: "primary.main",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      >
                        {highlight}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};
